/**
 * LeasingReturnPage — Leasingrückgabezentrum (Jun-2026, User-Wunsch).
 *
 * Der Händler prüft, ob ein Leasingfahrzeug besser zurückgegeben oder übernommen
 * (zum Ablösewert gekauft) wird. Nach der Entscheidung wird in beiden Fällen ein
 * Entwurf-Fahrzeug angelegt (kopiert, falls per FIN bereits vorhanden, sonst neu).
 */
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    RotateCcw, ArrowLeft, Loader2, Sparkles, Scale, KeyRound, Trash2, Car, RefreshCcw,
    History, Eye, X, UserCog, Moon, Mail, Inbox, Send, Users, Link2, Settings,
    MessageCircle, Printer, MailX, Calendar, FileText, User, Clock, Plus, Check, AlertCircle,
    FileSignature, Download, Megaphone, Phone, MapPin, Pencil, ShieldAlert, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import LeasingStammdaten from "./LeasingStammdaten";
import LeasingCalendar from "./LeasingCalendar";
import LeasingEmails from "./LeasingEmails";
import LeasingFreigabe from "./LeasingFreigabe";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import LeasingAutomationConfig from "../../components/leasing/LeasingSettingsDialog";
import DamageChecklist from "../../components/leasing/DamageChecklist";
import BankLogoBadge from "../../components/leasing/BankLogoBadge";
import { VinWarning } from "../../components/common/VinWarning";

const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso || "—"; } };

// Fälligkeits-Ampel nach Vertragsende: rot < 30 Tage, gelb < 60, grün später; grau = kein Datum.
const dueInfo = (end) => {
    if (!end) return { tone: "gray", label: "kein Datum", dot: "bg-gray-300" };
    const d = new Date(String(end).slice(0, 10));
    if (isNaN(d)) return { tone: "gray", label: "kein Datum", dot: "bg-gray-300" };
    const days = Math.ceil((d - new Date()) / 86400000);
    if (days < 0) return { tone: "red", label: `${Math.abs(days)} T. überfällig`, dot: "bg-red-500" };
    if (days < 30) return { tone: "red", label: `in ${days} T.`, dot: "bg-red-500" };
    if (days < 60) return { tone: "amber", label: `in ${days} T.`, dot: "bg-amber-400" };
    return { tone: "green", label: `in ${days} T.`, dot: "bg-emerald-500" };
};

const EMPTY = {
    customer: { first_name: "", last_name: "", email: "", phone: "" },
    vehicle: { manufacturer: "", model: "", category: "motorcycle", year: "", mileage: "", vin: "", condition: "used", color: "" },
    leasing_company: "", contract_number: "", contract_end: "",
    residual_value: "", market_value: "",
    agreed_km: "", actual_km: "", extra_km_cost: "0.10", under_km_refund: "0.05",
    return_repair_costs: "", notes: "", damage_items: [],
    damage_answers: {}, damage_estimate: null, return_appointment_date: "",
    customer_choice_message: "",
};

const str = (v) => (v === null || v === undefined ? "" : String(v));

// Arbeitsvermerk-Optionen (erweiterbar — „weitere folgen").
const WORK_NOTES = [
    "Mit Leasinggesellschaft klären",
    "Fahrzeug sicherstellen",
    "Gutachten veranlassen",
    "Rückgabetermin vereinbart",
    "Vertrag unterschrieben",
];

// Standard-Datumsfenster: 2 Monate Vergangenheit bis 2 Monate Zukunft.
const shiftMonths = (m) => { const d = new Date(); d.setMonth(d.getMonth() + m); return d.toISOString().slice(0, 10); };
const DEFAULT_END_FROM = shiftMonths(-2);
const DEFAULT_END_TO = shiftMonths(2);
const defaultFilters = () => ({ q: "", flag: "", contract_type: "", company: "", status: "", work_note: "", assignee: "", missing_residual: "", all_locations: false, end_from: DEFAULT_END_FROM, end_to: DEFAULT_END_TO });

const recToForm = (rec) => {
    const v = rec.vehicle || {};
    const c = rec.customer || {};
    return {
        customer: { first_name: str(c.first_name), last_name: str(c.last_name), email: str(c.email), phone: str(c.phone) },
        vehicle: {
            manufacturer: str(v.manufacturer || v.brand), model: str(v.model),
            category: v.category || "motorcycle", year: str(v.year), mileage: str(v.mileage),
            vin: str(v.vin), condition: v.condition || "used", color: str(v.color),
        },
        leasing_company: str(rec.leasing_company), contract_number: str(rec.contract_number),
        contract_end: rec.contract_end ? str(rec.contract_end).slice(0, 10) : "",
        residual_value: str(rec.residual_value), market_value: str(rec.market_value),
        agreed_km: str(rec.agreed_km), actual_km: str(rec.actual_km),
        extra_km_cost: str(rec.extra_km_cost), under_km_refund: str(rec.under_km_refund),
        return_repair_costs: str(rec.return_repair_costs), notes: str(rec.notes),
        customer_choice_message: str(rec.customer_choice_message),
        damage_items: Array.isArray(rec.damage_items)
            ? rec.damage_items.map((x) => ({
                area: str(x.area), description: str(x.description),
                amount: x.amount != null ? String(x.amount) : "",
                gw_article_id: x.gw_article_id || undefined, gw_sku: x.gw_sku || undefined,
            }))
            : [],
        damage_answers: rec.damage_answers || (rec.customer_damage_estimate && rec.customer_damage_estimate.answers) || {},
        damage_estimate: rec.damage_estimate || rec.customer_damage_estimate || null,
        return_appointment_date: rec.return_appointment_date ? str(rec.return_appointment_date).slice(0, 10) : "",
    };
};

const Fld = ({ label, children, testid }) => (
    <div>
        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</Label>
        <div className="mt-1" data-testid={testid}>{children}</div>
    </div>
);

function RecommendationCard({ a }) {
    if (!a) return null;
    // Aug-2026 (User-Wunsch): ohne Marktwert (= Kunde/Bewertung hat noch nicht
    // gerechnet) ist die Empfehlung „noch offen" statt irreführend „Rückgabe".
    if (!(Number(a.market_value) > 0)) {
        return (
            <div className="rounded-sm border border-gray-300 bg-gray-50 p-4" data-testid="leasing-recommendation">
                <div className="flex items-center gap-2">
                    <Scale className="w-5 h-5 text-gray-500" />
                    <h3 className="font-display font-black text-base text-gray-600" data-testid="leasing-recommendation-label">Empfehlung: noch offen</h3>
                </div>
                <p className="text-sm text-gray-600 mt-2">Noch keine Berechnung durchgeführt — sobald ein Marktwert erfasst ist (durch Bewertung oder den Online-Prozess des Kunden), wird Rückgabe vs. Übernahme automatisch berechnet.</p>
            </div>
        );
    }
    const takeover = a.recommendation === "takeover";
    return (
        <div className={`rounded-sm border p-4 ${takeover ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`} data-testid="leasing-recommendation">
            <div className="flex items-center gap-2 mb-2">
                <Scale className={`w-5 h-5 ${takeover ? "text-green-700" : "text-amber-700"}`} />
                <h3 className={`font-display font-black text-base ${takeover ? "text-green-800" : "text-amber-800"}`} data-testid="leasing-recommendation-label">
                    Empfehlung: {takeover ? "Übernahme" : "Rückgabe"}
                </h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">{a.rationale}</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="flex justify-between"><dt className="text-gray-500">km-Differenz</dt><dd className="font-mono-num font-semibold">{a.km_diff?.toLocaleString("de-DE")} km</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Mehrkilometer-Gebühr</dt><dd className="font-mono-num font-semibold">{eur(a.extra_km_fee)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Minderkilometer-Erstattung</dt><dd className="font-mono-num font-semibold">−{eur(a.under_km_credit)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Rückgabekosten gesamt</dt><dd className="font-mono-num font-semibold">{eur(a.return_charges)}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Eigenkapital bei Übernahme</dt><dd className="font-mono-num font-semibold">{eur(a.takeover_equity)}</dd></div>
                <div className="flex justify-between col-span-2 border-t pt-1 mt-1"><dt className="font-bold text-gray-700">Vorteil Übernahme</dt><dd className={`font-mono-num font-black ${a.advantage_takeover > 0 ? "text-green-700" : "text-amber-700"}`} data-testid="leasing-advantage">{eur(a.advantage_takeover)}</dd></div>
            </dl>
        </div>
    );
}

function DetailModal({ rec, onClose, onSaved }) {
    const [data, setData] = useState(rec);
    useEffect(() => { setData(rec); }, [rec]);
    if (!data) return null;
    const get = (path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), data);
    const applyLocal = (path, value) => {
        setData((prev) => {
            const next = { ...prev };
            const parts = path.split(".");
            if (parts.length === 1) { next[parts[0]] = value; }
            else { next[parts[0]] = { ...(next[parts[0]] || {}), [parts[1]]: value }; }
            return next;
        });
    };
    // Editierbare Zeile: Doppelklick → Eingabe, speichert bei Enter/Blur (Autospeichern).
    const EditRow = ({ k, path, type = "text", money = false }) => {
        const raw = get(path);
        const isNum = money || type === "number";
        const [editing, setEditing] = useState(false);
        const [val, setVal] = useState(raw ?? "");
        const [busy, setBusy] = useState(false);
        useEffect(() => { setVal(raw ?? ""); }, [raw]);
        const save = async () => {
            setEditing(false);
            if (String(val ?? "") === String(raw ?? "")) return;
            if (isNum && String(val).trim() !== "" && isNaN(Number(val))) {
                toast.error("Bitte eine gültige Zahl eingeben."); setVal(raw ?? ""); return;
            }
            setBusy(true);
            try {
                await api.post(`/leasing-returns/${data.id}/field`, { field: path, value: val });
                applyLocal(path, isNum ? (val === "" ? null : Number(val)) : (val || null));
                toast.success("Gespeichert.");
                onSaved && onSaved();
            } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht gespeichert werden."); setVal(raw ?? ""); }
            finally { setBusy(false); }
        };
        const display = raw === null || raw === undefined || raw === "" ? "—"
            : money ? eur(raw) : (type === "date" && raw ? fmt(raw) : String(raw));
        return (
            <div className="flex justify-between gap-4 py-1 border-b border-gray-100 text-sm group cursor-text"
                onDoubleClick={() => !editing && setEditing(true)}
                title="Doppelklick zum Bearbeiten"
                data-testid={`leasing-detail-row-${path}`}>
                <dt className="text-gray-500 flex items-center gap-1">{k}</dt>
                {editing ? (
                    <input autoFocus type={isNum ? "number" : type} value={val ?? ""}
                        onChange={(e) => setVal(e.target.value)}
                        onBlur={save}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(raw ?? ""); setEditing(false); } }}
                        className="font-semibold text-right border border-indigo-400 rounded-sm px-1.5 py-0.5 w-1/2 focus:outline-none"
                        data-testid={`leasing-detail-input-${path}`} />
                ) : (
                    <dd className="font-semibold text-right break-all inline-flex items-center gap-1">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        {display}
                        <Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100" />
                    </dd>
                )}
            </div>
        );
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="leasing-detail-modal">
            <div className="bg-white rounded-sm w-full max-w-lg my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h3 className="font-display font-black text-base">Vorgang-Details {data.source === "import_santander" && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-violet-100 text-violet-800 ml-2">Import Santander</span>}{data.source === "import_ca_bank" && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 ml-2">Import CA Bank</span>} <BankLogoBadge company={data.leasing_company} testid={`leasing-detail-bank-logo-${data.id}`} /></h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="leasing-detail-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
                    <div className="text-[11px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-sm px-2 py-1 mb-2 flex items-center gap-1"><Pencil className="w-3 h-3" /> Tipp: Werte per Doppelklick bearbeiten — wird automatisch gespeichert.</div>
                    <h4 className="text-[10px] uppercase font-black tracking-wider text-gray-400 mt-1 mb-1">Leasingnehmer</h4>
                    <dl>
                        <EditRow k="Anrede" path="customer.salutation" />
                        <EditRow k="Vorname" path="customer.first_name" />
                        <EditRow k="Nachname" path="customer.last_name" />
                        <EditRow k="E-Mail" path="customer.email" />
                        <EditRow k="Telefon" path="customer.phone" />
                        <EditRow k="Straße" path="customer.street" />
                        <EditRow k="PLZ" path="customer.plz" />
                        <EditRow k="Ort" path="customer.city" />
                        <EditRow k="Geburtsdatum" path="customer.birthdate" type="date" />
                    </dl>
                    <h4 className="text-[10px] uppercase font-black tracking-wider text-gray-400 mt-3 mb-1">Fahrzeug</h4>
                    <dl>
                        <EditRow k="Hersteller" path="vehicle.manufacturer" />
                        <EditRow k="Modell" path="vehicle.model" />
                        <EditRow k="FIN" path="vehicle.vin" />
                        <EditRow k="Kennzeichen" path="vehicle.plate" />
                        <EditRow k="Fahrzeugart" path="vehicle.vehicle_type" />
                        <EditRow k="Erstzulassung" path="vehicle.year" />
                        <EditRow k="km-Stand" path="vehicle.mileage" type="number" />
                    </dl>
                    <h4 className="text-[10px] uppercase font-black tracking-wider text-gray-400 mt-3 mb-1">Vertrag & Werte</h4>
                    <dl>
                        <EditRow k="Leasinggeber" path="leasing_company" />
                        <EditRow k="Vertragsnummer" path="contract_number" />
                        <EditRow k="Vertragsart" path="contract_type" />
                        <EditRow k="Vertragsende" path="contract_end" type="date" />
                        <EditRow k="Restwert / Ablöse" path="residual_value" money />
                        <EditRow k="Marktwert" path="market_value" money />
                        <EditRow k="Rate" path="rate" money />
                        <EditRow k="Saldo" path="saldo" money />
                        <EditRow k="Standort" path="location" />
                        <EditRow k="Verkäufer" path="seller" />
                    </dl>
                </div>
            </div>
        </div>
    );
}

const SA_LABELS = {
    last_name: "Nachname", first_name: "Vorname", birth_name: "Geburtsname",
    birth_date: "Geburtsdatum", birth_place: "Geburtsort", nationality: "Staatsangeh.",
    street: "Straße", zip_city: "PLZ / Ort", resident_since: "wohnhaft seit",
    previous_address: "Vorige Anschrift", phone: "Telefon", email: "E-Mail",
    marital_status: "Familienstand", children: "Kinder", housing: "Wohnverhältnis",
    occupation: "Beruf", employer: "Arbeitgeber", employer_address: "AG-Anschrift",
    employed_since: "beschäftigt seit", net_income: "Nettoeinkommen", rent: "Miete",
    other_rates: "sonst. Raten", account_holder: "Kontoinhaber", iban: "IBAN",
    bic: "BIC", bank_name: "Bank", id_authority: "Ausweisbehörde", id_number: "Ausweis-Nr.",
    id_issued: "ausgestellt", id_valid_until: "gültig bis",
};

function FinancingModal({ rec, onClose }) {
    const f = rec.takeover_financing || {};
    const sa = rec.selbstauskunft || {};
    const saEntries = Object.entries(SA_LABELS).filter(([k]) => (sa[k] ?? "") !== "");
    const forwarding = false;
    const openSaPdf = async () => {
        try {
            const res = await api.get(`/leasing-returns/${rec.id}/selbstauskunft.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { toast.error(e?.response?.data?.detail || "Selbstauskunft-PDF konnte nicht erstellt werden."); }
    };
    const FRow = ({ k, val }) => (
        <div className="flex justify-between gap-4 py-1 border-b border-gray-100 text-sm">
            <dt className="text-gray-500">{k}</dt>
            <dd className="font-bold text-right break-all">{val === null || val === undefined || val === "" ? "—" : val}</dd>
        </div>
    );
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="leasing-finance-modal" onClick={onClose}>
            <div className="bg-white rounded-sm w-full max-w-lg my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h3 className="font-display font-black text-base">Finanzierungswunsch &amp; Selbstauskunft</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="leasing-finance-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-4 py-3 max-h-[72vh] overflow-y-auto">
                    <div className="text-[11px] text-gray-500 mb-2">
                        {[rec.customer?.first_name, rec.customer?.last_name].filter(Boolean).join(" ")} · {rec.vehicle?.manufacturer} {rec.vehicle?.model}
                    </div>
                    <h4 className="text-[10px] uppercase font-black tracking-wider text-emerald-600 mt-1 mb-1">Ratenwunsch des Kunden</h4>
                    {Object.keys(f).length ? (
                        <dl>
                            <FRow k="Monatsrate" val={f.monthly_payment != null ? eur(f.monthly_payment) : ""} />
                            <FRow k="Laufzeit" val={f.term_months ? `${f.term_months} Monate` : ""} />
                            <FRow k="Anzahlung" val={f.down_payment != null ? eur(f.down_payment) : ""} />
                            <FRow k="Schlussrate (Ballon)" val={f.balloon_amount ? eur(f.balloon_amount) : (f.balloon_pct ? `${f.balloon_pct} %` : "—")} />
                            <FRow k="Effektivzins p.a." val={f.annual_rate != null ? `${f.annual_rate} %` : ""} />
                            <FRow k="Fahrzeugpreis (Restwert)" val={f.price != null ? eur(f.price) : ""} />
                            <FRow k="Gesamtbetrag" val={f.total_paid != null ? eur(f.total_paid) : ""} />
                        </dl>
                    ) : <div className="text-sm text-gray-400 py-2" data-testid="leasing-finance-none">Kein Finanzierungswunsch erfasst.</div>}

                    <h4 className="text-[10px] uppercase font-black tracking-wider text-indigo-600 mt-4 mb-1 flex items-center justify-between">
                        Digitale Selbstauskunft
                        {rec.selbstauskunft_at && <span className="text-[10px] font-bold text-gray-400 normal-case">ausgefüllt {fmt(rec.selbstauskunft_at)}</span>}
                    </h4>
                    {saEntries.length ? (
                        <>
                            <dl>{saEntries.map(([k, label]) => <FRow key={k} k={label} val={sa[k]} />)}</dl>
                            <button onClick={openSaPdf} data-testid="leasing-selbstauskunft-pdf-btn"
                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold rounded-sm px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700">
                                <FileText className="w-4 h-4" /> Selbstauskunft-PDF öffnen
                            </button>
                        </>
                    ) : <div className="text-sm text-gray-400 py-2" data-testid="leasing-selbstauskunft-none">Selbstauskunft noch nicht ausgefüllt.</div>}
                </div>
            </div>
        </div>
    );
}


function StatChip({ label, value, tone, icon, onClick, testid, active }) {
    const tones = {
        gray: "bg-gray-100 text-gray-700 hover:bg-gray-200",
        amber: "bg-amber-100 text-amber-800 hover:bg-amber-200",
        red: "bg-red-100 text-red-700 hover:bg-red-200",
        purple: "bg-purple-100 text-purple-800 hover:bg-purple-200",
        green: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200",
        indigo: "bg-indigo-100 text-indigo-800 hover:bg-indigo-200",
    };
    const rings = {
        gray: "ring-gray-500", amber: "ring-amber-500", red: "ring-red-500",
        purple: "ring-purple-500", green: "ring-emerald-500", indigo: "ring-indigo-500",
    };
    return (
        <button onClick={onClick} data-testid={testid} aria-pressed={!!active}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${tones[tone] || tones.gray} ${active ? `ring-2 ring-offset-1 ${rings[tone] || rings.gray}` : ""}`}>
            {icon && <UserCog className="w-3.5 h-3.5" />}
            <span>{label}</span>
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white/70 font-mono-num">{(value ?? 0).toLocaleString("de-DE")}</span>
        </button>
    );
}

function ContactAlt({ rec, onReload, resendOpen }) {
    const [busy, setBusy] = useState(false);
    const [newEmail, setNewEmail] = useState(rec.customer?.email || "");
    const showResend = !!resendOpen;
    const hasAnyEmail = !!rec.customer?.email;
    const hasEmail = hasAnyEmail && !rec.email_undeliverable;
    const hasPhone = !!rec.customer?.phone;

    const saveEmail = async () => {
        const email = newEmail.trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/leasing-returns/${rec.id}/set-email`, { email });
            toast.success(data.auto_sent
                ? "E-Mail nachgetragen – Einladung wurde gesendet."
                : "E-Mail nachgetragen – Einladungs-Entwurf liegt zur Freigabe bereit.");
            setNewEmail(email);
            onReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "E-Mail konnte nicht gespeichert werden.");
        } finally { setBusy(false); }
    };

    const toggleUndeliverable = async () => {
        setBusy(true);
        try {
            await api.post(`/leasing-returns/${rec.id}/email-undeliverable`, { value: !rec.email_undeliverable });
            toast.success(rec.email_undeliverable ? "E-Mail wieder als zustellbar markiert." : "E-Mail als nicht zustellbar markiert.");
            onReload?.();
        } catch { toast.error("Aktion fehlgeschlagen."); } finally { setBusy(false); }
    };
    const waLink = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/leasing-returns/${rec.id}/whatsapp`);
            if (data.wa_link) window.open(data.wa_link, "_blank", "noopener");
            else toast.error("Keine gültige Handynummer.");
        } catch { toast.error("WhatsApp-Link fehlgeschlagen."); } finally { setBusy(false); }
    };
    const waSend = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/leasing-returns/${rec.id}/whatsapp/send`, {});
            toast.success(data.mocked ? "WhatsApp vorbereitet (Versand gemockt – Twilio-Key fehlt)." : "WhatsApp gesendet.");
        } catch (e) { toast.error(e?.response?.data?.detail || "WhatsApp-Versand fehlgeschlagen."); } finally { setBusy(false); }
    };
    const printLetter = async () => {
        setBusy(true);
        try {
            const res = await api.get(`/leasing-returns/${rec.id}/letter.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error("Anschreiben konnte nicht erstellt werden."); } finally { setBusy(false); }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-dashed" data-testid={`leasing-contact-alt-${rec.id}`}>
            <span className="text-[10px] uppercase font-black tracking-wider text-gray-400">Kontakt</span>
            {!hasEmail && (
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700" data-testid={`leasing-noemail-${rec.id}`}>
                    {rec.email_undeliverable ? "E-Mail unzustellbar" : "Keine E-Mail"}
                </span>
            )}
            {!hasAnyEmail && (
                <div className="inline-flex items-center gap-1" data-testid={`leasing-add-email-box-${rec.id}`}>
                    <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEmail(); }}
                        placeholder="Neue E-Mail-Adresse"
                        disabled={busy}
                        className="border border-indigo-300 rounded-sm px-2 py-1 text-xs w-48 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        data-testid={`leasing-add-email-input-${rec.id}`}
                    />
                    <button onClick={saveEmail} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60" data-testid={`leasing-add-email-send-${rec.id}`} title="E-Mail nachtragen & Einladung erzeugen">
                        <Send className="w-3.5 h-3.5" /> Nachtragen & einladen
                    </button>
                </div>
            )}
            {!hasEmail && hasPhone && (
                <>
                    <button onClick={waLink} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 border border-green-300 text-green-700 hover:bg-green-50" data-testid={`leasing-wa-link-${rec.id}`} title="WhatsApp mit vorbereitetem Text öffnen">
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp öffnen
                    </button>
                    <button onClick={waSend} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 border border-green-300 text-green-700 hover:bg-green-50" data-testid={`leasing-wa-send-${rec.id}`} title="WhatsApp direkt senden (Twilio, aktuell gemockt)">
                        <Send className="w-3.5 h-3.5" /> WA senden
                    </button>
                </>
            )}
            {!hasEmail && !hasPhone && (
                <button onClick={printLetter} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 border border-gray-300 text-gray-700 hover:bg-gray-50" data-testid={`leasing-letter-${rec.id}`} title="Postalisches Anschreiben drucken">
                    <Printer className="w-3.5 h-3.5" /> Anschreiben drucken
                </button>
            )}
            {hasAnyEmail && showResend && (
                <div className="inline-flex items-center gap-1" data-testid={`leasing-resend-box-${rec.id}`}>
                    <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEmail(); }}
                        placeholder="E-Mail-Adresse"
                        disabled={busy}
                        className="border border-gray-300 rounded-sm px-2 py-1 text-xs w-48 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        data-testid={`leasing-resend-input-${rec.id}`}
                        title="E-Mail-Adresse (änderbar) für den erneuten Versand"
                    />
                    <button onClick={saveEmail} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60" data-testid={`leasing-resend-send-${rec.id}`} title="Einladungs-/Options-Link erneut an diese Adresse senden">
                        <Send className="w-3.5 h-3.5" /> Link erneut senden
                    </button>
                </div>
            )}
            {!!rec.customer?.email && (
                <button onClick={toggleUndeliverable} disabled={busy} className={`inline-flex items-center gap-1 text-xs font-bold rounded-sm px-2 py-1 border ${rec.email_undeliverable ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-gray-300 text-gray-500 hover:bg-gray-50"}`} data-testid={`leasing-undeliverable-${rec.id}`} title="E-Mail-Zustellbarkeit umschalten">
                    <MailX className="w-3.5 h-3.5" /> {rec.email_undeliverable ? "Wieder zustellbar" : "E-Mail unzustellbar"}
                </button>
            )}
        </div>
    );
}

const ACT_META = {
    record: { Icon: FileText, color: "text-slate-600", bg: "bg-slate-100", label: "Datensatz" },
    email: { Icon: Mail, color: "text-blue-600", bg: "bg-blue-100", label: "Kommunikation" },
    appointment: { Icon: Calendar, color: "text-sky-600", bg: "bg-sky-100", label: "Termin" },
    customer: { Icon: User, color: "text-emerald-600", bg: "bg-emerald-100", label: "Kunde" },
};
const ACTOR_LABEL = { customer: "Kunde", dealer: "Händler", employee: "Mitarbeiter", system: "System" };

function ActivityTimeline({ acts, recId }) {
    if (acts === null) return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />;
    if (acts.length === 0) return <div className="text-xs text-gray-400" data-testid={`leasing-activity-empty-${recId}`}>Noch keine Aktivitäten erfasst.</div>;
    return (
        <ol className="relative space-y-2.5" data-testid={`leasing-activity-list-${recId}`}>
            {acts.map((a) => {
                const m = ACT_META[a.kind] || ACT_META.record;
                const Icon = m.Icon;
                return (
                    <li key={a.id} className="flex items-start gap-2.5" data-testid={`leasing-activity-${a.id}`}>
                        <span className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 ${m.bg} ${m.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-bold text-xs text-black">{a.title}</span>
                                <span className="inline-flex items-center gap-1 text-[10px] text-gray-400"><Clock className="w-2.5 h-2.5" />{fmt(a.created_at)}</span>
                                <span className="text-[10px] uppercase font-bold px-1 py-0.5 rounded-sm bg-gray-100 text-gray-500">{ACTOR_LABEL[a.actor] || a.actor}</span>
                            </div>
                            {a.detail && <div className="text-xs text-gray-600 mt-0.5 break-words">{a.detail}</div>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function CustomerProgress({ rec }) {
    const isTakeover = rec.customer_choice === "takeover";
    const s1 = !!rec.customer_choice;
    const s2 = !!(rec.customer_confirmed_at || rec.selbstauskunft_at);
    const s3 = isTakeover
        ? !!rec.forms_signed_at
        : !!(rec.has_return_appointment || rec.return_appointment_date || (rec.return_appointment && rec.return_appointment.date));
    const steps = [
        { label: "Auswahl", done: s1 },
        { label: "Daten", done: s2 },
        { label: isTakeover ? "Signatur" : "Termin", done: s3 },
    ];
    if (!(s1 || s2 || s3)) {
        return <div className="mt-1.5 text-[10px] text-gray-400 italic" data-testid={`leasing-progress-${rec.id}`}>Kunde hat den Online-Prozess noch nicht begonnen.</div>;
    }
    return (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`leasing-progress-${rec.id}`}>
            <span className="text-[9px] uppercase font-bold tracking-wider text-gray-400 mr-0.5">Im Prozess</span>
            {steps.map((st, i) => (
                <div key={st.label} className="flex items-center gap-1.5">
                    <span className={`w-4 h-4 rounded-full grid place-items-center text-[8px] font-bold ${st.done ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"}`}>{st.done ? "✓" : i + 1}</span>
                    <span className={`text-[10px] font-semibold ${st.done ? "text-green-700" : "text-gray-400"}`}>{st.label}</span>
                    {i < steps.length - 1 && <span className={`w-4 h-0.5 ${st.done ? "bg-green-400" : "bg-gray-200"}`} />}
                </div>
            ))}
        </div>
    );
}

function SavedRow({ rec, onDecide, onDelete, onEdit, onUndo, onDetails, onAssign, onSnooze, onWorkNote, onInvite, onCopyLink, onReload, onShowFinance, assignees, editing, busy }) {
    const decided = rec.status === "decided";
    const snoozedUntil = rec.snoozed_until && new Date(rec.snoozed_until) > new Date() ? rec.snoozed_until : null;
    const [docOpen, setDocOpen] = useState(false);
    const [resendOpen, setResendOpen] = useState(false);
    const hasAnyEmail = !!rec.customer?.email;
    const [acts, setActs] = useState(null);
    // Jun-2026 (User-Wunsch) — Arbeitsvermerk-Aktionen (Pflichtfelder, Sicherstellung, Termin).
    const REQUIRE_DETAIL = { "Mit Leasinggesellschaft klären": "Was ist mit der Leasinggesellschaft zu klären?", "Gutachten veranlassen": "Was soll begutachtet werden? (Auftrag/Details)" };
    const [noteBusy, setNoteBusy] = useState(false);
    const [detailFor, setDetailFor] = useState(null);      // aktiver Pflichttext-Vermerk
    const [detailVal, setDetailVal] = useState("");
    const [apptOpen, setApptOpen] = useState(false);
    const [apptDate, setApptDate] = useState(rec.return_appointment_date || "");
    const [apptTime, setApptTime] = useState(rec.return_appointment_time || "");
    const handleWorkNoteSelect = async (val) => {
        if (!val) { onWorkNote(rec, "", ""); setDetailFor(null); setApptOpen(false); return; }
        if (REQUIRE_DETAIL[val]) { setDetailFor(val); setDetailVal(rec.work_note === val ? (rec.work_note_detail || "") : ""); return; }
        if (val === "Rückgabetermin vereinbart") { setApptOpen(true); onWorkNote(rec, val, ""); return; }
        if (val === "Fahrzeug sicherstellen") {
            setNoteBusy(true);
            try {
                const { data } = await api.post(`/leasing-returns/${rec.id}/secure-vehicle`, {});
                toast.success(data.message || "Sicherstellungs-E-Mail erzeugt.");
                onWorkNote(rec, val, "");
            } catch (e) { toast.error(e.response?.data?.detail || "Sicherstellung fehlgeschlagen."); }
            finally { setNoteBusy(false); }
            return;
        }
        onWorkNote(rec, val, "");   // z. B. „Vertrag unterschrieben"
    };
    const saveNoteDetail = async () => {
        if (!detailVal.trim()) { toast.error("Bitte eine Beschreibung eingeben."); return; }
        setNoteBusy(true);
        try { await onWorkNote(rec, detailFor, detailVal.trim()); setDetailFor(null); }
        catch { /* Fehler-Toast kommt aus onWorkNote */ }
        finally { setNoteBusy(false); }
    };
    const bookAppt = async () => {
        if (!apptDate) { toast.error("Bitte ein Datum wählen."); return; }
        setNoteBusy(true);
        try {
            const { data } = await api.post(`/leasing-returns/${rec.id}/appointment`, { date: apptDate, time: apptTime });
            toast.success(data.mail_sent ? "Termin gebucht — Bestätigung an Kunden gesendet." : "Termin gebucht.");
            setApptOpen(false); onReload && onReload();
        } catch (e) { toast.error(e.response?.data?.detail || "Termin konnte nicht gebucht werden."); }
        finally { setNoteBusy(false); }
    };

    const loadActs = async () => {
        try {
            const { data } = await api.get(`/leasing-returns/${rec.id}/activities`);
            setActs(data.items || []);
        } catch { setActs([]); }
    };
    const toggleDoc = async () => {
        const next = !docOpen;
        setDocOpen(next);
        if (next) loadActs();
    };
    const exportActs = async (kind) => {
        try {
            const res = await api.get(`/leasing-returns/${rec.id}/activities.${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            if (kind === "pdf") {
                window.open(url, "_blank", "noopener");
            } else {
                const a = document.createElement("a");
                a.href = url; a.download = `Verlauf-${rec.contract_number || rec.id.slice(0, 8)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error("Export fehlgeschlagen."); }
    };
    const openVollmacht = async () => {
        try {
            const res = await api.get(`/leasing-returns/${rec.id}/vollmacht.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error("Vollmacht konnte nicht erzeugt werden."); }
    };
    const openDoc = async (path, label) => {
        try {
            const res = await api.get(path, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { toast.error(`${label} konnte nicht erzeugt werden.`); }
    };
    const copyVollmachtLink = async () => {
        try {
            const { data } = await api.get(`/leasing-returns/${rec.id}/landing-url`);
            const base = (data.url || "").replace(/\/leasing\/[^/?#]+.*$/, "");
            const token = (data.url || "").match(/\/leasing\/([^/?#]+)/)?.[1];
            const link = token ? `${base}/api/public/leasing/${token}/vollmacht.pdf` : null;
            if (!link) { toast.error("Link nicht verfügbar."); return; }
            try { await navigator.clipboard.writeText(link); toast.success("Vollmacht-Link kopiert: " + link); }
            catch { toast.message("Vollmacht-Link", { description: link, duration: 12000 }); }
        } catch (e) { toast.error(e.response?.data?.detail || "Link konnte nicht geladen werden."); }
    };
    return (
        <div className={`border rounded-sm p-3 bg-white ${editing ? "ring-2 ring-indigo-500 border-indigo-400" : ""}`} data-testid={`leasing-row-${rec.id}`}>
            <div
                className={`flex items-start justify-between gap-3 ${decided ? "" : "cursor-pointer"}`}
                onClick={() => !decided && onEdit(rec)}
                data-testid={`leasing-edit-${rec.id}`}
                title={decided ? undefined : "Zum Bearbeiten anklicken"}
            >
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dueInfo(rec.contract_end).dot}`} title={`Vertragsende ${dueInfo(rec.contract_end).label}`} data-testid={`leasing-due-${rec.id}`} />
                        <Car className="w-4 h-4 text-gray-500 shrink-0" />
                        <span className="font-bold text-sm truncate">{rec.vehicle?.manufacturer} {rec.vehicle?.model || "—"}</span>
                        <BankLogoBadge company={rec.leasing_company} testid={`leasing-bank-logo-${rec.id}`} />
                        {rec.record_type === "finance" && (
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`leasing-finance-badge-${rec.id}`} title={rec.finance_product ? `Finanzierungsprodukt: ${rec.finance_product}` : "Finanzierungsvertrag"}>
                                Finanzierung{rec.finance_product ? ` · ${rec.finance_product}` : ""}
                            </span>
                        )}
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm ${decided ? "bg-gray-200 text-gray-700" : "bg-blue-100 text-blue-800"}`}>
                            {decided ? (rec.decision === "takeover" ? "Übernommen" : "Zurückgegeben") : "Offen"}
                        </span>
                        {rec.contract_type && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-700" title={rec.contract_type === "RW" ? "Restwertvertrag" : "Kilometervertrag"}>{rec.contract_type}</span>}
                        {rec.contacted_by_email && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800" data-testid={`leasing-flag-contacted-${rec.id}`}>Angeschrieben</span>}
                        {rec.has_return_appointment && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-800" data-testid={`leasing-flag-appointment-${rec.id}`}>Termin</span>}
                        {rec.customer_choice && (() => {
                            const hasFin = !!(rec.takeover_financing || rec.selbstauskunft || rec.takeover_payment_method === "financing");
                            const label = `Kunde: ${rec.customer_choice === "takeover" ? "Übernahme" : "Rückgabe"}${rec.customer_choice === "takeover" && rec.takeover_payment_method ? ` · ${rec.takeover_payment_method === "financing" ? "Finanzierung" : "Überweisung"}` : ""}`;
                            return hasFin ? (
                                <button onClick={() => onShowFinance?.(rec)} data-testid={`leasing-choice-${rec.id}`} title="Finanzierungswunsch & Selbstauskunft ansehen" className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer">
                                    {label} <Eye className="w-3 h-3" />
                                </button>
                            ) : (
                                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800" data-testid={`leasing-choice-${rec.id}`} title="Vom Kunden im Online-Prozess gewählt">{label}</span>
                            );
                        })()}
                        {rec.assignee_name && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800" data-testid={`leasing-assignee-${rec.id}`}><UserCog className="w-3 h-3" />{rec.assignee_name}</span>}
                        {rec.work_note && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-teal-100 text-teal-800" data-testid={`leasing-worknote-${rec.id}`}>{rec.work_note}</span>}
                        {snoozedUntil && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800" data-testid={`leasing-snoozed-${rec.id}`}><Moon className="w-3 h-3" />bis {fmt(snoozedUntil)}</span>}
                        {rec.is_foreign && <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-800 border border-orange-300" data-testid={`leasing-foreign-${rec.id}`} title="Vorgang eines anderen Standorts — Bearbeitung nur am zuständigen Standort"><MapPin className="w-3 h-3" />{rec.standort_name || "anderer Standort"}</span>}
                    </div>
                    <div className="text-sm text-gray-800 font-medium mt-1.5 leading-relaxed">
                        <span className="font-bold text-black">{[rec.customer?.first_name, rec.customer?.last_name].filter(Boolean).join(" ") || "—"}</span>
                        {rec.contract_number && <> · Vertrag <span className="font-mono-num">{rec.contract_number}</span></>}
                        {rec.contract_end && <> · Ende <span className="font-mono-num">{fmt(rec.contract_end)}</span></>}
                        {rec.vehicle?.vin && <> · FIN <span className="font-mono-num">{rec.vehicle.vin}</span></>}
                        {" · "}{rec.record_type === "finance" ? "Schlussrate/Restwert" : "Ablöse"} <span className="font-bold text-black">{eur(rec.residual_value)}</span>{rec.record_type !== "finance" && <> · Markt {eur(rec.market_value)}</>}
                        {rec.record_type !== "finance" && <>{" · "}Empfehlung: {Number(rec.market_value) > 0
                            ? <span className="font-bold" style={{ color: rec.recommendation === "takeover" ? "#15803d" : "#b45309" }}>{rec.recommendation === "takeover" ? "Übernahme" : "Rückgabe"}</span>
                            : <span className="font-bold text-gray-500" title="Noch keine Berechnung durchgeführt (kein Marktwert erfasst)" data-testid={`leasing-rec-open-${rec.id}`}>noch offen</span>}</>}
                        {decided && rec.vehicle_internal_number && (
                            <> · Fahrzeug <Link to={rec.vehicle_id ? `/edit-listing/${rec.vehicle_id}` : "/dealer/disposition"} className="text-blue-600 underline font-mono-num">{rec.vehicle_internal_number}</Link>{rec.vehicle_copied ? " (kopiert)" : " (neu)"}</>
                        )}
                    </div>
                    {(rec.customer?.phone || rec.customer?.email || rec.customer?.street || rec.customer?.plz || rec.customer?.city) && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-600 max-h-[3.6em] overflow-hidden" data-testid={`leasing-contact-${rec.id}`}>
                            {rec.customer?.phone && <a href={`tel:${rec.customer.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:text-[#111827]" data-testid={`leasing-contact-phone-${rec.id}`}><Phone className="w-3 h-3 text-gray-400" />{rec.customer.phone}</a>}
                            {rec.customer?.email && <a href={`mailto:${rec.customer.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:text-[#111827] truncate max-w-[240px]" data-testid={`leasing-contact-email-${rec.id}`}><Mail className="w-3 h-3 text-gray-400" />{rec.customer.email}</a>}
                            {(rec.customer?.street || rec.customer?.plz || rec.customer?.city) && (
                                <span className="inline-flex items-center gap-1" data-testid={`leasing-contact-address-${rec.id}`}>
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    {[rec.customer?.street, [rec.customer?.plz, rec.customer?.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                                </span>
                            )}
                        </div>
                    )}
                    {!decided && <CustomerProgress rec={rec} />}
                </div>
                <div className="text-[10px] text-gray-400 shrink-0 text-right">
                    <div>{fmt(rec.created_at)}</div>
                </div>
            </div>
            {rec.assignment_note && (
                <div className="mt-2 flex items-start gap-1.5 rounded-sm border-2 border-amber-400 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900" data-testid={`leasing-assign-note-display-${rec.id}`}>
                    <Megaphone className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span><b>Info für {rec.assignee_name || "Bearbeiter"}:</b> {rec.assignment_note}</span>
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t" data-testid={`leasing-tools-${rec.id}`}>
                <select
                    value={rec.work_note || ""}
                    onChange={(e) => handleWorkNoteSelect(e.target.value)}
                    disabled={noteBusy}
                    className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white max-w-[200px]"
                    data-testid={`leasing-worknote-select-${rec.id}`}
                    title="Arbeitsvermerk"
                >
                    <option value="">— Arbeitsvermerk —</option>
                    {WORK_NOTES.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
                <div className="inline-flex items-center gap-1">
                    <UserCog className="w-3.5 h-3.5 text-gray-400" />
                    <select
                        value={rec.assignee_id || ""}
                        onChange={(e) => onAssign(rec, e.target.value)}
                        className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white max-w-[160px]"
                        data-testid={`leasing-assign-select-${rec.id}`}
                        title="Bearbeiter zuweisen"
                    >
                        <option value="">— kein Bearbeiter —</option>
                        {(assignees || []).map((a) => <option key={a.id} value={a.id}>{a.name}{a.is_owner ? " (ich)" : (a.standort ? ` · ${a.standort}` : "")}</option>)}
                    </select>
                    {rec.assignee_id && <LeasingAssignNote rec={rec} onReload={onReload} />}
                </div>
                <div className="inline-flex items-center gap-1">
                    <Moon className="w-3.5 h-3.5 text-gray-400" />
                    <select
                        value=""
                        onChange={(e) => { if (e.target.value) onSnooze(rec, e.target.value); }}
                        className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white"
                        data-testid={`leasing-snooze-select-${rec.id}`}
                        title="Vorgang schlummern lassen"
                    >
                        <option value="">{snoozedUntil ? `Schlummert bis ${fmt(snoozedUntil)}` : "Schlummern…"}</option>
                        <option value="1d">1 Tag</option>
                        <option value="3d">3 Tage</option>
                        <option value="1w">1 Woche</option>
                        <option value="3w">3 Wochen</option>
                        {snoozedUntil && <option value="clear">Schlummern beenden</option>}
                    </select>
                </div>
                <div className="flex flex-wrap gap-2 ml-auto items-center">
                    <Button onClick={(e) => { e.stopPropagation(); onDetails(rec); }} variant="outline" className="rounded-sm text-xs h-8 border-gray-300 text-gray-700 font-bold" data-testid={`leasing-details-${rec.id}`} title="Alle Daten ansehen">
                        <Eye className="w-4 h-4 mr-1.5" /> Details
                    </Button>
                    <Button onClick={(e) => { e.stopPropagation(); toggleDoc(); }} variant="outline" className="rounded-sm text-xs h-8 border-gray-300 text-gray-700 font-bold" data-testid={`leasing-doc-toggle-${rec.id}`} title="Vollständiger Verlauf (E-Mails, Termine, Änderungen, Kundenaktivitäten)">
                        <History className="w-4 h-4 mr-1.5" /> Verlauf {docOpen ? "▲" : "▼"}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="rounded-sm text-xs h-8 border-gray-300 text-gray-700 font-bold" data-testid={`leasing-vollmacht-${rec.id}`} title="Vorausgefüllte Dokumente drucken">
                                <FileSignature className="w-4 h-4 mr-1.5" /> Dokumente
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openDoc(`/leasing-returns/${rec.id}/form/rueckgabeprotokoll.pdf`, "Rückgabeprotokoll"); }} data-testid={`leasing-doc-protokoll-${rec.id}`}>Rückgabeprotokoll</DropdownMenuItem>
                            {rec.contract_type === "RW" && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openDoc(`/leasing-returns/${rec.id}/form/einverstaendnis.pdf`, "Einverständniserklärung"); }} data-testid={`leasing-doc-einverstaendnis-${rec.id}`}>Einverständniserklärung</DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openVollmacht(); }} data-testid={`leasing-doc-vollmacht-${rec.id}`}>Vollmacht (bei Fremdanlieferung)</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {decided ? (
                        <>
                            {rec.decision === "takeover" && rec.vehicle_id && (
                                <Link to={`/dealer/disposition?q=${encodeURIComponent(rec.vehicle_internal_number || (rec.vehicle || {}).vin || "")}`}
                                    className="rounded-sm text-xs h-8 inline-flex items-center px-3 font-bold bg-green-700 hover:bg-green-800 text-white"
                                    data-testid={`leasing-open-dispo-${rec.id}`}
                                    title="Fahrzeug im Verkauf/Kaufvertrag (Disposition) öffnen">
                                    <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Im Verkauf öffnen
                                </Link>
                            )}
                            <Button onClick={() => onUndo(rec)} disabled={busy} variant="outline" className="rounded-sm text-xs h-8 border-gray-300 text-gray-700" data-testid={`leasing-undo-${rec.id}`}>
                                <History className="w-3.5 h-3.5 mr-1.5" /> Abwicklung rückgängig
                            </Button>
                        </>
                    ) : (
                        <>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button disabled={busy} variant="outline" className="rounded-sm text-xs h-8 border-gray-300 text-gray-700" data-testid={`leasing-link-menu-${rec.id}`} title="Landingpage-Link kopieren oder erneut senden">
                                        <Link2 className="w-3.5 h-3.5 mr-1.5" /> Link
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="text-xs">
                                    <DropdownMenuItem onClick={() => onCopyLink(rec)} data-testid={`leasing-copylink-${rec.id}`}>Link kopieren</DropdownMenuItem>
                                    {hasAnyEmail && (
                                        <DropdownMenuItem onClick={() => setResendOpen((v) => !v)} data-testid={`leasing-resend-menu-${rec.id}`}>Link erneut senden…</DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {rec.work_note === "Vertrag unterschrieben" && (
                                <Link to={`/dealer/disposition?q=${encodeURIComponent((rec.vehicle || {}).vin || rec.contract_number || "")}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded-sm text-xs h-8 inline-flex items-center px-3 font-bold bg-[#111827] hover:bg-black text-white"
                                    data-testid={`leasing-to-sales-${rec.id}`}
                                    title="Vertrag ist unterschrieben — im Verkaufsprozess (Disposition) fortfahren">
                                    <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Zum Verkaufsprozess
                                </Link>
                            )}
                            <Button onClick={() => onDelete(rec)} disabled={busy} variant="ghost" className="rounded-sm text-xs h-8 text-gray-500" data-testid={`leasing-delete-${rec.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Löschen
                            </Button>
                            {/* Aug-2026 (User-Wunsch): EIN Abwickeln-Button statt drei —
                                öffnet denselben Weg, den auch der Leasingnehmer nimmt. */}
                            <Button onClick={() => onDecide(rec, null)} disabled={busy} className="rounded-sm text-xs h-8 bg-green-700 hover:bg-green-800 text-white font-bold" data-testid={`leasing-open-process-${rec.id}`} title="Kunden-Landingpage öffnen und dort Rückgabe oder Übernahme abwickeln — derselbe Weg wie für den Leasingnehmer">
                                <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Abwickeln
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {detailFor && (
                <div className="mt-2 rounded-sm border-2 border-indigo-300 bg-indigo-50 p-2.5" data-testid={`leasing-note-detail-${rec.id}`}>
                    <div className="text-[11px] font-bold text-indigo-800 mb-1">{detailFor} — Pflichtangabe</div>
                    <Textarea value={detailVal} onChange={(e) => setDetailVal(e.target.value)} rows={2} placeholder={REQUIRE_DETAIL[detailFor]} className="rounded-sm text-sm bg-white" data-testid={`leasing-note-detail-input-${rec.id}`} />
                    <div className="flex gap-2 mt-2">
                        <Button onClick={saveNoteDetail} disabled={noteBusy} className="rounded-sm h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid={`leasing-note-detail-save-${rec.id}`}>{noteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Speichern</Button>
                        <Button onClick={() => setDetailFor(null)} variant="ghost" className="rounded-sm h-8 text-xs text-gray-500" data-testid={`leasing-note-detail-cancel-${rec.id}`}>Abbrechen</Button>
                    </div>
                </div>
            )}
            {apptOpen && (
                <div className="mt-2 rounded-sm border-2 border-sky-300 bg-sky-50 p-2.5 flex flex-wrap items-end gap-2" data-testid={`leasing-appt-${rec.id}`}>
                    <div>
                        <div className="text-[11px] font-bold text-sky-800 mb-1">Rückgabetermin</div>
                        <input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid={`leasing-appt-date-${rec.id}`} />
                    </div>
                    <input type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid={`leasing-appt-time-${rec.id}`} />
                    <Button onClick={bookAppt} disabled={noteBusy} className="rounded-sm h-8 text-xs bg-sky-600 hover:bg-sky-700 text-white font-bold" data-testid={`leasing-appt-save-${rec.id}`}>{noteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5 mr-1" />} Termin buchen &amp; bestätigen</Button>
                    <Button onClick={() => setApptOpen(false)} variant="ghost" className="rounded-sm h-8 text-xs text-gray-500" data-testid={`leasing-appt-cancel-${rec.id}`}>Schließen</Button>
                </div>
            )}
            {rec.work_note_detail && !detailFor && (
                <div className="mt-2 flex items-start gap-1.5 rounded-sm border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs text-teal-900" data-testid={`leasing-worknote-detail-display-${rec.id}`}>
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span><b>{rec.work_note}:</b> {rec.work_note_detail}</span>
                </div>
            )}
            {!decided && <ContactAlt rec={rec} onReload={onReload} resendOpen={resendOpen} />}
            {docOpen && (
                <div className="mt-3 pt-3 border-t" data-testid={`leasing-doc-${rec.id}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase font-black tracking-wider text-gray-400">Verlauf · alle Aktivitäten</div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => exportActs("csv")} className="text-[10px] text-gray-400 hover:text-gray-700 inline-flex items-center gap-1" data-testid={`leasing-activity-csv-${rec.id}`} title="Verlauf als CSV exportieren"><FileText className="w-3 h-3" /> CSV</button>
                            <button onClick={() => exportActs("pdf")} className="text-[10px] text-gray-400 hover:text-gray-700 inline-flex items-center gap-1" data-testid={`leasing-activity-pdf-${rec.id}`} title="Verlauf als PDF (z. B. für die Leasinggesellschaft)"><Printer className="w-3 h-3" /> PDF</button>
                            <button onClick={loadActs} className="text-[10px] text-gray-400 hover:text-gray-700 inline-flex items-center gap-1" data-testid={`leasing-activity-refresh-${rec.id}`}><RefreshCcw className="w-3 h-3" /> Aktualisieren</button>
                        </div>
                    </div>
                    <ActivityTimeline acts={acts} recId={rec.id} />
                </div>
            )}
        </div>
    );
}

export default function LeasingReturnPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("vorgaenge"); // vorgaenge | kalender | settings
    const [settingsTab, setSettingsTab] = useState("kontakt"); // kontakt | stammdaten | emails | freigabe
    const [focusTpl, setFocusTpl] = useState(null); // Feld-Sprung „Vorlage bearbeiten"
    // Jul-2026 (User-Wunsch) — Finanzierungsausläufer als eigener Bereich.
    const [recordType, setRecordType] = useState("leasing"); // leasing | finance
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [financeRec, setFinanceRec] = useState(null);
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
    const autoSaveTimer = useRef(null);
    const skipAutoSave = useRef(true);
    const [matchedSurcharge, setMatchedSurcharge] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [filters, setFilters] = useState(defaultFilters);
    const [pageSize, setPageSize] = useState(20);
    const [assignees, setAssignees] = useState([]);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total: 0, pages: 1, companies: [] });
    const [stats, setStats] = useState(null);
    const [reconciling, setReconciling] = useState(false);

    const reconcile = async () => {
        setReconciling(true);
        try {
            const { data } = await api.post("/leasing-returns/reconcile-customers");
            toast.success(`Kundenstamm-Abgleich: ${data.matched} Treffer · ${data.filled_email} E-Mails, ${data.filled_phone} Telefonnummern ergänzt.`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Abgleich fehlgeschlagen."); }
        finally { setReconciling(false); }
    };

    const loadStats = () => api.get("/leasing-returns/stats").then((r) => setStats(r.data)).catch(() => {});

    const load = async (p = page, f = filters, ps = pageSize) => {
        loadStats();
        try {
            const params = { page: p, page_size: ps };
            if (f.q) params.q = f.q;
            if (f.flag) params.flag = f.flag;
            if (f.contract_type) params.contract_type = f.contract_type;
            if (f.company) params.company = f.company;
            if (f.status) params.status = f.status;
            if (f.work_note) params.work_note = f.work_note;
            if (f.assignee) params.assignee = f.assignee;
            if (f.end_from) params.end_from = f.end_from;
            if (f.end_to) params.end_to = f.end_to;
            if (f.missing_residual) params.missing_residual = true;
            if (f.all_locations && f.q) params.all_locations = true;
            params.record_type = recordType;
            const { data } = await api.get("/leasing-returns", { params });
            setItems(data.items || []);
            setMeta({ total: data.total ?? 0, pages: data.pages ?? 1, companies: data.companies || [] });
        } catch { /* noop */ }
    };
    useEffect(() => {
        const t = setTimeout(() => { load(1, filters, pageSize); setPage(1); }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, pageSize, recordType]);
    useEffect(() => { load(page, filters, pageSize); /* eslint-disable-next-line */ }, [page]);
    useEffect(() => { api.get("/leasing-returns/assignees").then((r) => setAssignees(r.data.items || [])).catch(() => {}); }, []);
    // Jun-2026 (User-Wunsch) — Deep-Link vom Leasing-Badge im Dispo-Board:
    // ?q=<FIN> öffnet den Vorgang direkt (Datumsfenster dafür aufheben).
    const [urlParams] = useSearchParams();
    useEffect(() => {
        const q = (urlParams.get("q") || "").trim();
        if (q) { setFilters({ ...defaultFilters(), q, end_from: "", end_to: "" }); setPage(1); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Aug-2026: Deep-Link „Vorlage bearbeiten" aus der zentralen E-Mail-Freigabe
    // (?settings=texte&focus=leasing_<stage>) → Texte-Tab öffnen + Feld hervorheben.
    useEffect(() => {
        if (urlParams.get("settings") === "texte") {
            const f = (urlParams.get("focus") || "").replace(/^leasing_/, "").replace(/^reminder\d?$/, "reminder");
            setTab("settings"); setSettingsTab("freigabe");
            if (f) setFocusTpl(f);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Aufschlag automatisch zur FIN ermitteln (gelernte Regel je FIN-Präfix-10).
    useEffect(() => {
        const vin = (form.vehicle.vin || "").trim();
        if (vin.length < 6) { setMatchedSurcharge(null); return; }
        const t = setTimeout(() => {
            api.get("/dealer/leasing/decode-vin", { params: { vin } })
                .then((r) => setMatchedSurcharge(r.data?.matched_rule?.surcharge ?? null))
                .catch(() => setMatchedSurcharge(null));
        }, 400);
        return () => clearTimeout(t);
    }, [form.vehicle.vin]);
    // GW-Schadensermittlung: Summe der Positionen automatisch in „Rückgabe-/Schadenkosten" übernehmen.
    useEffect(() => {
        const di = form.damage_items;
        if (!Array.isArray(di) || di.length === 0) return;
        const total = di.reduce((s, x) => s + (Number(x.amount) || 0), 0);
        const cur = Number(form.return_repair_costs) || 0;
        if (Math.round(total) !== Math.round(cur)) setF("return_repair_costs", String(Math.round(total)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(form.damage_items)]);
    const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

    const setC = (k, v) => setForm((p) => ({ ...p, customer: { ...p.customer, [k]: v } }));
    const setV = (k, v) => setForm((p) => ({ ...p, vehicle: { ...p.vehicle, [k]: v } }));
    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const estimateMarket = async () => {
        if (!form.vehicle.manufacturer.trim() || !form.vehicle.model.trim()) {
            toast.error("Bitte Hersteller und Modell für die KI-Schätzung angeben.");
            return;
        }
        setAiBusy(true);
        try {
            const { data } = await api.post("/listings/ai-price-suggest", {
                brand: form.vehicle.manufacturer, model: form.vehicle.model,
                year: form.vehicle.year, mileage: form.vehicle.mileage,
                condition: form.vehicle.condition, category: form.vehicle.category,
            });
            const val = data.suggested_b2c ?? data.range_max ?? data.suggested_b2b;
            if (val) { setF("market_value", String(Math.round(val))); toast.success(`KI-Marktwert: ${eur(val)}${data.mocked ? " (Demo)" : ""}`); }
            else { toast.message("Keine KI-Schätzung verfügbar — bitte Marktwert manuell eintragen."); }
        } catch { toast.error("KI-Schätzung fehlgeschlagen."); }
        finally { setAiBusy(false); }
    };

    const startEdit = (rec) => {
        skipAutoSave.current = true;
        setEditingId(rec.id);
        setForm(recToForm(rec));
        setSaveState("saved");
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const resetForm = () => {
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        skipAutoSave.current = true;
        setEditingId(null); setForm(EMPTY); setFormOpen(false); setSaveState("idle");
    };
    const openNewForm = () => {
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        skipAutoSave.current = true;
        setTab("vorgaenge"); setRecordType("leasing");
        setForm(EMPTY); setEditingId(null); setSaveState("idle"); setFormOpen(true);
    };

    // Deep-Link aus der Auswertung („Potenziale") — ?open={rec_id} öffnet den Vorgang.
    const openedDeepLink = useRef(false);
    useEffect(() => {
        if (openedDeepLink.current) return;
        const recId = new URLSearchParams(window.location.search).get("open");
        if (!recId) return;
        openedDeepLink.current = true;
        api.get(`/leasing-returns/${recId}`)
            .then(({ data }) => { setTab("vorgaenge"); startEdit(data.return || data); })
            .catch(() => toast.error("Vorgang nicht gefunden."));
        // ?open aus der URL entfernen (Reload-Schleifen vermeiden)
        const next = new URLSearchParams(window.location.search);
        next.delete("open");
        navigate({ search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Autospeichern: jede Änderung am Formular wird verzögert gespeichert (PATCH bei
    // Bearbeitung, ansonsten POST sobald Mindestdaten vorhanden sind).
    const autoSave = async () => {
        const hasData = editingId || (form.contract_number || "").trim() || (form.vehicle.manufacturer || "").trim()
            || (form.vehicle.vin || "").trim() || (form.customer.last_name || "").trim();
        if (!hasData) { setSaveState("idle"); return; }
        setSaveState("saving");
        try {
            if (editingId) {
                await api.patch(`/leasing-returns/${editingId}`, form);
            } else {
                const { data } = await api.post("/leasing-returns", form);
                skipAutoSave.current = true;
                setEditingId(data.id);
            }
            setSaveState("saved");
            await load(page, filters);
        } catch (e) {
            setSaveState("error");
            toast.error(e.response?.data?.detail || "Autospeichern fehlgeschlagen.");
        }
    };

    useEffect(() => {
        if (!formOpen && !editingId) return;
        if (skipAutoSave.current) { skipAutoSave.current = false; return; }
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => { autoSave(); }, 900);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(form), formOpen, editingId]);

    const decide = async (rec, decision) => {
        setBusy(true);
        try {
            const { data } = await api.get(`/leasing-returns/${rec.id}/landing-url`);
            const url = decision ? `${data.url}${data.url.includes("?") ? "&" : "?"}flow=${decision}` : data.url;
            window.open(url, "_blank", "noopener");
            toast.success(decision
                ? `${decision === "takeover" ? "Übernahme" : "Rückgabe"}-Prozess geöffnet — Kunden-Landingpage im neuen Tab. Das Fahrzeug wird nach Abschluss automatisch ins Inventar übernommen.`
                : "Kundenprozess geöffnet — Rückgabe oder Übernahme dort abwickeln (derselbe Weg wie für den Leasingnehmer).");
        } catch (e) { toast.error(e.response?.data?.detail || "Prozess konnte nicht geöffnet werden."); }
        finally { setBusy(false); }
    };

    const undo = async (rec) => {
        setBusy(true);
        try {
            await api.post(`/leasing-returns/${rec.id}/undo`);
            toast.success("Abwicklung rückgängig gemacht · Vorgang wieder offen.");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Rückgängig fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const assign = async (rec, assigneeId) => {
        try {
            const { data } = await api.post(`/leasing-returns/${rec.id}/assign`, { assignee_id: assigneeId });
            toast.success(data.assignee_name ? `Zugewiesen an ${data.assignee_name}.` : "Zuweisung aufgehoben.");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen."); }
    };

    const snooze = async (rec, duration) => {
        try {
            await api.post(`/leasing-returns/${rec.id}/snooze`, { duration });
            toast.success(duration === "clear" ? "Schlummern beendet." : "Vorgang schlummert.");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Schlummern fehlgeschlagen."); }
    };

    const prepareInvite = async (rec) => {
        if (!(rec.customer || {}).email) {
            toast.error("Keine Kunden-E-Mail hinterlegt. Bitte zuerst per „Kunden abgleichen“ ergänzen oder im Vorgang eintragen.");
            return;
        }
        try {
            await api.post(`/leasing-returns/${rec.id}/outbox`, { type: "invite" });
            toast.success("Einladung erzeugt — in der zentralen E-Mail-Freigabe (Bereich „Leasing“) prüfen und freigeben.");
        } catch (e) { toast.error(e.response?.data?.detail || "Entwurf fehlgeschlagen."); }
    };

    const copyLandingLink = async (rec) => {
        try {
            const { data } = await api.get(`/leasing-returns/${rec.id}/landing-url`);
            const url = data.url;
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Landingpage-Link kopiert: " + url);
            } catch {
                // Clipboard nicht verfügbar (z. B. ohne HTTPS) → Link im Toast anzeigen
                toast.message("Landingpage-Link", { description: url, duration: 12000 });
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Link konnte nicht geladen werden."); }
    };

    const setWorkNote = async (rec, note, detail = "") => {
        try {
            await api.post(`/leasing-returns/${rec.id}/work-note`, { work_note: note, detail });
            toast.success(note ? `Arbeitsvermerk: ${note}` : "Arbeitsvermerk entfernt.");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); throw e; }
    };

    const [deleteFor, setDeleteFor] = useState(null);
    const remove = (rec) => setDeleteFor(rec);

    // Soft-Delete mit Pflicht-Löschgrund (User-Wunsch Jul-2026): Datensatz wird
    // ausgeblendet und erst nach 18 Monaten endgültig entfernt.
    const confirmDelete = async (reason) => {
        if (!deleteFor) return;
        setBusy(true);
        try {
            await api.delete(`/leasing-returns/${deleteFor.id}`, { data: { reason } });
            toast.success("Vorgang gelöscht — wird nach 18 Monaten endgültig entfernt.");
            setDeleteFor(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50" data-testid="leasing-return-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                                <RotateCcw className="w-4 h-4 text-[#FFCC00]" /> Anschlussgeschäft
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight min-w-0 [overflow-wrap:anywhere]" data-testid="leasing-page-title">
                                    Leasingrückgabezentrum
                                </h1>
                                <button type="button" onClick={() => setTab("settings")}
                                    className={`inline-flex items-center justify-center rounded-full p-2.5 transition-colors ${tab === "settings" ? "bg-[#FFCC00] text-[#111827]" : "bg-white/10 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-[#111827]"}`}
                                    data-testid="leasing-settings-btn" title="Einstellungen — Kontaktstrecke, Stammdaten, E-Mails & Texte">
                                    <Settings className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-300 mt-1 max-w-2xl">
                                Prüfen Sie, ob sich für den Leasingnehmer die <b>Rückgabe</b> oder die <b>Übernahme</b> (Kauf zum Ablösewert) mehr lohnt.
                                Nach der Entscheidung wird das Fahrzeug automatisch als <b>Entwurf im Inventar</b> angelegt.
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-3 min-w-0 ml-auto">
                            <div className="flex gap-2 flex-wrap items-center justify-end max-w-full" data-testid="leasing-nav-row">
                                <Link to="/dashboard" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm font-bold" data-testid="leasing-back">
                                    ← Dashboard
                                </Link>
                                <Link to="/dealer/financing-expiries" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="leasing-link-financing">
                                    Finanzierungs-Ausläufe →
                                </Link>
                                <Link to="/dealer/leads" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="leasing-link-leads">
                                    Leads →
                                </Link>
                                <Link to="/dealer/email-freigabe?source=leasing" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="leasing-link-freigabe">
                                    E-Mail-Freigabe →
                                </Link>
                            </div>
                        </div>
                    </div>
                    {/* Aug-2026: Tab-Leiste — Einstellungen bündelt Stammdaten/Import/Center,
                        E-Mails & Freigabe/Texte (Zugriff über das Zahnrad neben dem Titel). */}
                    <div className="mt-5 flex items-center gap-2 flex-wrap justify-end" data-testid="leasing-tabs">
                        {[["vorgaenge", "Vorgänge"]].map(([k, l]) => (
                            <button key={k} type="button"
                                onClick={() => { setTab(k); if (k === "vorgaenge") setRecordType("leasing"); }}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${tab === k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-transparent text-gray-300 border-gray-600 hover:border-[#FFCC00]"}`}
                                data-testid={`leasing-tab-${k}`}>
                                {l}
                            </button>
                        ))}
                        {/* Aug-2026 (User-Wunsch): „Neuer Vorgang" + Aktualisieren zwischen Vorgänge und E-Mails */}
                        {!(editingId || formOpen) && (
                            <Button onClick={openNewForm} className="rounded-sm h-[30px] text-xs bg-white text-[#111827] hover:bg-gray-100 font-bold uppercase tracking-wider" data-testid="leasing-new-vorgang">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Neuer Vorgang
                            </Button>
                        )}
                        <button type="button" onClick={() => load(page, filters)} className="text-gray-300 hover:text-[#FFCC00] border border-gray-600 hover:border-[#FFCC00] rounded-sm p-1.5" title="Aktualisieren" data-testid="leasing-refresh">
                            <RefreshCcw className="w-4 h-4" />
                        </button>
                        {[["emails", "E-Mails"], ["kalender", "Kalender"]].map(([k, l]) => (
                            <button key={k} type="button"
                                onClick={() => { setTab(k); if (k === "vorgaenge") setRecordType("leasing"); }}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${tab === k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-transparent text-gray-300 border-gray-600 hover:border-[#FFCC00]"}`}
                                data-testid={`leasing-tab-${k}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">

            {tab === "kalender" ? <LeasingCalendar onOpenVorgang={(rec) => { setTab("vorgaenge"); startEdit(rec); }} /> :
             tab === "emails" ? <LeasingEmails /> :
             tab === "settings" ? (
                <div className="space-y-4" data-testid="leasing-settings-view">
                    <div className="flex flex-wrap items-center gap-2">
                        {[["kontakt", "Kontaktstrecke"], ["stammdaten", "Stammdaten, Import & Center"], ["freigabe", "Texte"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setSettingsTab(k)}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${settingsTab === k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                data-testid={`leasing-setting-tab-${k}`}>{l}</button>
                        ))}
                        <Button onClick={reconcile} disabled={reconciling} variant="outline" className="rounded-sm h-8 text-xs ml-auto" data-testid="leasing-reconcile" title="Fehlende E-Mail/Telefon aus dem Kundenstamm ergänzen">
                            {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Users className="w-3.5 h-3.5 mr-1" />} Kundenstamm-Abgleich
                        </Button>
                    </div>
                    {settingsTab === "kontakt" ? <div className="bg-white border rounded-sm p-4"><LeasingAutomationConfig onEditTemplate={(tpl) => { setFocusTpl(tpl); setSettingsTab("freigabe"); }} /></div> :
                     settingsTab === "stammdaten" ? <LeasingStammdaten view="all" /> :
                     settingsTab === "emails" ? <LeasingEmails /> :
                     <LeasingFreigabe focusField={focusTpl} />}
                </div>
             ) : (
            <>
            {(editingId || formOpen) ? (
            <div className="grid grid-cols-1 gap-6">
                {/* Eingabeformular */}
                <div className="space-y-3">
                    {editingId && (
                        <div className="flex items-center justify-between gap-3 rounded-sm border border-indigo-300 bg-indigo-50 px-3 py-2" data-testid="lr-edit-banner">
                            <span className="text-sm font-bold text-indigo-800">Bearbeitungsmodus — Änderungen werden automatisch gespeichert.</span>
                            <button onClick={resetForm} className="text-xs font-bold text-indigo-700 hover:underline" data-testid="lr-edit-cancel">Abbrechen</button>
                        </div>
                    )}
                    <section className="border rounded-sm p-3 bg-white">
                        <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm inline-block mb-3">Leasingnehmer</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">
                            <Fld label="Vorname" testid="lr-first-name"><Input value={form.customer.first_name} onChange={(e) => setC("first_name", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Nachname" testid="lr-last-name"><Input value={form.customer.last_name} onChange={(e) => setC("last_name", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Telefon" testid="lr-phone"><Input value={form.customer.phone} onChange={(e) => setC("phone", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="E-Mail" testid="lr-email"><Input type="email" value={form.customer.email} onChange={(e) => setC("email", e.target.value)} className="rounded-sm" /></Fld>
                        </div>
                    </section>

                    <section className="border rounded-sm p-3 bg-white">
                        <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm inline-block mb-3">Fahrzeug</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">
                            <Fld label="Hersteller" testid="lr-manufacturer"><Input value={form.vehicle.manufacturer} onChange={(e) => setV("manufacturer", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Modell" testid="lr-model"><Input value={form.vehicle.model} onChange={(e) => setV("model", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Fahrzeugart" testid="lr-category">
                                <select value={form.vehicle.category} onChange={(e) => setV("category", e.target.value)} className="w-full h-9 rounded-sm border border-gray-300 px-2 text-sm bg-white">
                                    <option value="motorcycle">Motorrad</option>
                                    <option value="bicycle">Fahrrad</option>
                                </select>
                            </Fld>
                            <Fld label="Erstzulassung (Jahr)" testid="lr-year"><Input type="number" value={form.vehicle.year} onChange={(e) => setV("year", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="km-Stand" testid="lr-mileage"><Input type="number" value={form.vehicle.mileage} onChange={(e) => setV("mileage", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="Farbe" testid="lr-color"><Input value={form.vehicle.color} onChange={(e) => setV("color", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Fahrgestellnummer (FIN)" testid="lr-vin"><Input value={form.vehicle.vin} onChange={(e) => setV("vin", e.target.value)} className="rounded-sm font-mono-num uppercase" placeholder="für Dublettenprüfung" /><VinWarning value={form.vehicle.vin} /></Fld>
                        </div>
                    </section>

                    <section className="border rounded-sm p-3 bg-white">
                        <h3 className="text-xs font-black uppercase tracking-wider bg-[#111827] text-white px-2 py-1 rounded-sm inline-block mb-3">Leasingvertrag & Werte</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">
                            <Fld label="Leasinggeber" testid="lr-leasing-company"><Input value={form.leasing_company} onChange={(e) => setF("leasing_company", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Vertragsnummer" testid="lr-contract-number"><Input value={form.contract_number} onChange={(e) => setF("contract_number", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Vertragsende" testid="lr-contract-end"><Input type="date" value={form.contract_end} onChange={(e) => setF("contract_end", e.target.value)} className="rounded-sm" /></Fld>
                            <Fld label="Ablösewert / Restwert (€)" testid="lr-residual"><Input type="number" value={form.residual_value} onChange={(e) => setF("residual_value", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="Marktwert (€)" testid="lr-market">
                                <div className="flex gap-1">
                                    <Input type="number" value={form.market_value} onChange={(e) => setF("market_value", e.target.value)} className="rounded-sm font-mono-num" />
                                    <Button type="button" onClick={estimateMarket} disabled={aiBusy} variant="outline" className="rounded-sm h-9 px-2 shrink-0" title="KI-Marktwert schätzen" data-testid="lr-ai-market">
                                        {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </Fld>
                            <Fld label="Restwert inkl. Aufschlag (€)" testid="lr-residual-plus-surcharge">
                                <div className="relative">
                                    <Input
                                        type="text"
                                        readOnly
                                        value={eur((Number(form.residual_value) || 0) + (Number(matchedSurcharge) || 0))}
                                        className="rounded-sm font-mono-num bg-gray-50 cursor-not-allowed"
                                        data-testid="lr-residual-plus-surcharge-input"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-500 mt-1" data-testid="lr-surcharge-hint">
                                    {matchedSurcharge != null
                                        ? <>+ {eur(matchedSurcharge)} Aufschlag (Regel zur FIN gefunden)</>
                                        : "Kein Aufschlag zur FIN hinterlegt"}
                                </p>
                            </Fld>
                            <Fld label="Vereinbarte km" testid="lr-agreed-km"><Input type="number" value={form.agreed_km} onChange={(e) => setF("agreed_km", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="Gefahrene km" testid="lr-actual-km"><Input type="number" value={form.actual_km} onChange={(e) => setF("actual_km", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <div />
                            <Fld label="€ je Mehrkilometer" testid="lr-extra-km"><Input type="number" step="0.01" value={form.extra_km_cost} onChange={(e) => setF("extra_km_cost", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="€ je Minderkilometer (Erstattung)" testid="lr-under-km"><Input type="number" step="0.01" value={form.under_km_refund} onChange={(e) => setF("under_km_refund", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="Rückgabe-/Schadenkosten (€)" testid="lr-repair"><Input type="number" value={form.return_repair_costs} onChange={(e) => setF("return_repair_costs", e.target.value)} className="rounded-sm font-mono-num" /></Fld>
                            <Fld label="Rückgabetermin" testid="lr-return-appointment"><Input type="date" value={form.return_appointment_date} onChange={(e) => setF("return_appointment_date", e.target.value)} className="rounded-sm" /></Fld>
                        </div>
                        <p className="text-[10px] text-gray-500 -mt-1">Ein gesetztes Datum markiert den Vorgang als „Termin vorhanden" und zeigt ihn im Kalender.</p>
                        <DamageChecklist key={editingId || "new"} vehicle={form.vehicle} answers={form.damage_answers} onChange={(ans, est) => { setF("damage_answers", ans); if (est) { setF("damage_estimate", est); setF("return_repair_costs", String(Math.round(est.total || 0))); } }} />
                        {editingId && (
                            <div className="mt-3 border-2 border-blue-200 bg-blue-50/60 rounded-sm p-3" data-testid="lr-customer-message">
                                <div className="text-[11px] uppercase font-bold text-blue-700 inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Optionale Nachricht des Kunden (von der Landingpage)</div>
                                {form.customer_choice_message
                                    ? <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{form.customer_choice_message}</p>
                                    : <p className="text-sm text-gray-400 mt-1 italic">Noch keine Nachricht vom Kunden.</p>}
                            </div>
                        )}
                        <div className="mt-3">
                            <Fld label="Notizen (nur intern)" testid="lr-notes"><Textarea value={form.notes} onChange={(e) => setF("notes", e.target.value)} rows={2} className="rounded-sm" placeholder="Interne Vermerke – nicht für den Kunden sichtbar" /></Fld>
                        </div>
                        <div className="flex flex-wrap justify-end items-center gap-2 mt-4 pt-3 border-t">
                            {editingId && (
                                <Button type="button" onClick={openNewForm} variant="ghost" className="rounded-sm text-gray-500 mr-auto" data-testid="lr-new">
                                    <RotateCcw className="w-4 h-4 mr-2" /> Neuer Vorgang
                                </Button>
                            )}
                            <div className="inline-flex items-center gap-2 text-sm" data-testid="lr-autosave-status">
                                {saveState === "saving" && <span className="text-gray-500 inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Speichert…</span>}
                                {saveState === "saved" && <span className="text-emerald-600 font-bold inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> Automatisch gespeichert</span>}
                                {saveState === "error" && <button type="button" onClick={autoSave} className="text-red-600 font-bold inline-flex items-center gap-1.5" data-testid="lr-autosave-retry"><AlertCircle className="w-4 h-4" /> Fehler – erneut speichern</button>}
                                {saveState === "idle" && <span className="text-gray-400 inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> Änderungen werden automatisch gespeichert</span>}
                            </div>
                            <Button type="button" onClick={resetForm} variant="outline" className="rounded-sm" data-testid="lr-close-form">
                                Schließen
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
            ) : null}
            <div className="mt-10">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-lg font-black">Gespeicherte Vorgänge ({meta.total.toLocaleString("de-DE")})</h2>
                </div>

                {stats && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="leasing-stats-bar">
                        <StatChip label="Offen" value={stats.offen} tone="gray" testid="leasing-stat-offen"
                            active={filters.flag === "open"}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "open", status: "", end_from: "", end_to: "" })); setPage(1); }} />
                        <StatChip label="Fällig < 30 T." value={stats.faellig_30} tone="amber" testid="leasing-stat-faellig"
                            active={!filters.flag && !filters.status && filters.end_from === stats.today && filters.end_to === stats.plus30}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "", status: "", end_from: stats.today, end_to: stats.plus30 })); setPage(1); }} />
                        <StatChip label="Überfällig" value={stats.ueberfaellig} tone="red" testid="leasing-stat-ueberfaellig"
                            active={!filters.flag && !filters.status && !filters.end_from && filters.end_to === stats.today}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "", status: "", end_from: "", end_to: stats.today })); setPage(1); }} />
                        <StatChip label="Schlummern" value={stats.schlummernd} tone="purple" testid="leasing-stat-schlummern"
                            active={filters.flag === "snoozed"}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "snoozed", status: "", end_from: "", end_to: "" })); setPage(1); }} />
                        <StatChip label="Im Prozess" value={stats.im_prozess} tone="indigo" testid="leasing-stat-im-prozess"
                            active={filters.flag === "in_process"}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "in_process", status: "", end_from: "", end_to: "" })); setPage(1); }} />
                        <StatChip label="Abgewickelt" value={stats.abgewickelt} tone="green" testid="leasing-stat-abgewickelt"
                            active={filters.status === "decided"}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "", status: "decided", end_from: "", end_to: "" })); setPage(1); }} />
                        <StatChip label="Erledigt (grün/rot/Alt)" value={stats.erledigt} tone="gray" testid="leasing-stat-erledigt"
                            active={filters.flag === "erledigt"}
                            onClick={() => { setFilters((p) => ({ ...p, flag: "erledigt", status: "", end_from: "", end_to: "" })); setPage(1); }} />
                        <div className="w-px bg-gray-200 mx-1 self-stretch" />
                        {stats.per_assignee.slice(0, 6).map((a) => (
                            <StatChip key={a.assignee_id} label={a.assignee_name} value={a.count} tone="indigo" icon testid={`leasing-stat-assignee-${a.assignee_id}`}
                                active={filters.assignee === a.assignee_id}
                                onClick={() => { setFilters((p) => ({ ...p, assignee: a.assignee_id, flag: "", status: "", end_from: "", end_to: "" })); setPage(1); }} />
                        ))}
                    </div>
                )}

                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="leasing-filter-bar">
                    <input value={filters.q} onChange={(e) => setFilter("q", e.target.value)} placeholder="Suche: Kunde, FIN, Vertrag, Modell, Bank, Ort, Telefon …" className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm flex-1 min-w-[220px]" data-testid="leasing-filter-q" />
                    <label className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-sm border px-2.5 py-1.5 cursor-pointer ${filters.all_locations ? "border-[#111827] bg-[#111827] text-white" : "border-gray-300 text-gray-600 bg-white"}`} title="Suche über alle Standorte der Organisation (nur mit Suchbegriff)" data-testid="leasing-filter-all-locations">
                        <input type="checkbox" checked={!!filters.all_locations} onChange={(e) => setFilter("all_locations", e.target.checked)} className="accent-[#FFCC00]" />
                        <MapPin className="w-3.5 h-3.5" /> Alle Standorte
                    </label>
                    <select value={filters.flag} onChange={(e) => setFilter("flag", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-flag">
                        <option value="">Alle Status</option>
                        <option value="open">Offen</option>
                        <option value="contacted">Angeschrieben</option>
                        <option value="appointment">Termin vorhanden</option>
                        <option value="snoozed">Schlummernde</option>
                        <option value="return_done">Rückgabe abgewickelt</option>
                        <option value="takeover_done">Übernahme abgewickelt</option>
                    </select>
                    <select value={filters.contract_type} onChange={(e) => setFilter("contract_type", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-contract">
                        <option value="">Alle Verträge</option>
                        <option value="RW">RW (Restwert)</option>
                        <option value="KM">KM (Kilometer)</option>
                    </select>
                    <select value={filters.work_note} onChange={(e) => setFilter("work_note", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-worknote">
                        <option value="">Alle Arbeitsvermerke</option>
                        {WORK_NOTES.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                    <select value={filters.assignee} onChange={(e) => setFilter("assignee", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-assignee">
                        <option value="">Alle Bearbeiter</option>
                        {(assignees || []).map((a) => <option key={a.id} value={a.id}>{a.name}{a.is_owner ? " (ich)" : (a.standort ? ` · ${a.standort}` : "")}</option>)}
                    </select>
                    <div className="flex items-center gap-1 text-sm">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Ende</span>
                        <input type="date" value={filters.end_from} onChange={(e) => setFilter("end_from", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-end-from" title="Vertragsende von" />
                        <span className="text-gray-400">–</span>
                        <input type="date" value={filters.end_to} onChange={(e) => setFilter("end_to", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-end-to" title="Vertragsende bis" />
                    </div>
                    {meta.companies.length > 1 && (
                        <select value={filters.company} onChange={(e) => setFilter("company", e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-filter-company">
                            <option value="">Alle Gesellschaften</option>
                            {meta.companies.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    <button type="button" onClick={() => setFilter("missing_residual", filters.missing_residual ? "" : "1")}
                        className={`border rounded-sm px-3 py-1.5 text-sm font-bold ${filters.missing_residual ? "bg-red-600 border-red-600 text-white" : "bg-white border-gray-300 text-gray-600 hover:border-red-400"}`}
                        data-testid="leasing-filter-missing-residual"
                        title="Nur Vorgänge ohne Restwert/Ablöse anzeigen — hier fehlen die Bankdaten (gezielt nachfordern)">
                        Restwert fehlt
                    </button>
                    <div className="inline-flex items-center gap-1 text-xs text-gray-500 ml-auto" data-testid="leasing-page-size">
                        <span className="font-bold uppercase tracking-wider text-[10px]">Pro Seite</span>
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid="leasing-page-size-select">
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                    <button onClick={() => setFilters(defaultFilters())} className="text-xs font-bold text-gray-500 hover:text-gray-900 px-2" data-testid="leasing-filter-clear">Zurücksetzen</button>
                </div>

                {items.length === 0 ? (
                    <div className="text-center py-10 text-gray-500 border-2 border-dashed border-gray-200 rounded-sm" data-testid="leasing-empty">
                        Keine Vorgänge für die aktuelle Auswahl.
                    </div>
                ) : (
                    <>
                    <div className="space-y-3" data-testid="leasing-list">
                        {items.map((rec) => <SavedRow key={rec.id} rec={rec} onDecide={decide} onDelete={remove} onEdit={startEdit} onUndo={undo} onDetails={setDetail} onAssign={assign} onSnooze={snooze} onWorkNote={setWorkNote} onInvite={prepareInvite} onCopyLink={copyLandingLink} onReload={() => load()} onShowFinance={setFinanceRec} assignees={assignees} editing={editingId === rec.id} busy={busy} />)}
                    </div>
                    {meta.pages > 1 && (
                        <div className="flex items-center justify-center gap-3 mt-5" data-testid="leasing-pagination">
                            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="text-sm font-bold border border-gray-300 rounded-sm px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50" data-testid="leasing-page-prev">Zurück</button>
                            <span className="text-sm text-gray-600" data-testid="leasing-page-info">Seite {page} / {meta.pages}</span>
                            <button disabled={page >= meta.pages} onClick={() => setPage((p) => Math.min(meta.pages, p + 1))} className="text-sm font-bold border border-gray-300 rounded-sm px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50" data-testid="leasing-page-next">Weiter</button>
                        </div>
                    )}
                    </>
                )}
            </div>
            </>
            )}
            </div>
            <DetailModal rec={detail} onClose={() => setDetail(null)} onSaved={() => load()} />
            {financeRec && <FinancingModal rec={financeRec} onClose={() => setFinanceRec(null)} />}
            {deleteFor && (
                <DeleteReasonModal rec={deleteFor} busy={busy}
                    onCancel={() => setDeleteFor(null)} onConfirm={confirmDelete} />
            )}
        </div>
    );
}

// Lösch-Modal mit Pflicht-Grund (User-Wunsch Jul-2026) — schließt NICHT bei Klick außerhalb.
function DeleteReasonModal({ rec, busy, onCancel, onConfirm }) {
    const [reason, setReason] = useState("");
    const veh = rec.vehicle || {};
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="leasing-delete-modal">
            <div className="bg-white rounded-sm border border-gray-200 shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-2">
                    <Trash2 className="w-5 h-5 text-red-600" />
                    <h3 className="font-black text-lg text-[#111827]">Vorgang löschen</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                    <b>{[veh.brand, veh.model].filter(Boolean).join(" ") || "Vorgang"}</b>
                    {rec.contract_number ? ` · Vertrag ${rec.contract_number}` : ""} wird ausgeblendet
                    und erst nach <b>18 Monaten endgültig gelöscht</b>.
                </p>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Löschgrund (Pflicht)</label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={300}
                    placeholder="z. B. Doppelt angelegt, Kunde hat Vertrag verlängert, Fehleingabe…"
                    className="mt-1 rounded-sm" data-testid="leasing-delete-reason" />
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onCancel} disabled={busy} className="rounded-sm text-xs font-bold" data-testid="leasing-delete-cancel">Abbrechen</Button>
                    <Button onClick={() => onConfirm(reason.trim())} disabled={busy || !reason.trim()}
                        className="rounded-sm text-xs font-bold bg-red-600 hover:bg-red-700 text-white" data-testid="leasing-delete-confirm">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Endgültig ausblenden
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Kurzinfo für den zugewiesenen Bearbeiter (User-Wunsch Jul-2026) — Speichern bei Enter/Verlassen.
function LeasingAssignNote({ rec, onReload }) {
    const [val, setVal] = useState(rec.assignment_note || "");
    useEffect(() => { setVal(rec.assignment_note || ""); }, [rec.assignment_note]);
    const save = async () => {
        const next = val.trim();
        if ((rec.assignment_note || "") === next) return;
        try {
            await api.post(`/leasing-returns/${rec.id}/assign`, { assignee_id: rec.assignee_id, note: next });
            toast.success("Kurzinfo für Bearbeiter gespeichert.");
            onReload && onReload();
        } catch (e) { toast.error(e.response?.data?.detail || "Kurzinfo konnte nicht gespeichert werden."); }
    };
    return (
        <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            placeholder="Kurzinfo für Bearbeiter…" maxLength={300}
            className="border border-amber-300 bg-amber-50/60 rounded-sm px-2 py-1 text-xs max-w-[190px] focus:border-[#111827] focus:outline-none"
            title="Kurze Info, die der zugewiesene Bearbeiter deutlich am Vorgang sieht"
            data-testid={`leasing-assign-note-${rec.id}`} />
    );
}
